import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Building, CreditCard, Landmark, Link2Off, Loader2, Plus, RefreshCw, Trash2, Wallet, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useAccounts, type Account } from '@/hooks/useFinancialData';
import { usePluggy } from '@/hooks/usePluggy';
import { formatBRL, formatDate } from '@/lib/format';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';

// Widget da Pluggy carregado sob demanda (não entra no bundle inicial)
const PluggyConnect = lazy(() =>
  import('react-pluggy-connect').then((m) => ({ default: m.PluggyConnect }))
);

// ─── Confirm dialog simples ───────────────────────────────────────────────────

interface ConfirmProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

function ConfirmDialog({ message, onConfirm, onCancel, loading }: ConfirmProps) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card p-6 max-w-sm w-full shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-negative/10 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-negative" />
          </div>
          <p className="text-sm text-ink">{message}</p>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button onClick={onConfirm} className="btn bg-negative hover:bg-negative/90 text-white" disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirmar exclusão
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers de exclusão ──────────────────────────────────────────────────────

async function deleteAccount(accountId: string, tenantId: string) {
  // Apaga transações primeiro (on delete restrict)
  const { error: txErr } = await supabase
    .from('transactions')
    .delete()
    .eq('account_id', accountId)
    .eq('tenant_id', tenantId);
  if (txErr) throw new Error('Erro ao apagar transações: ' + txErr.message);

  const { error: accErr } = await supabase
    .from('accounts')
    .delete()
    .eq('id', accountId)
    .eq('tenant_id', tenantId);
  if (accErr) throw new Error('Erro ao apagar conta: ' + accErr.message);
}

async function deleteAllData(tenantId: string) {
  // Apaga todas as transações do tenant
  const { error: txErr } = await supabase
    .from('transactions')
    .delete()
    .eq('tenant_id', tenantId);
  if (txErr) throw new Error('Erro ao apagar transações: ' + txErr.message);

  // Apaga todas as contas
  const { error: accErr } = await supabase
    .from('accounts')
    .delete()
    .eq('tenant_id', tenantId);
  if (accErr) throw new Error('Erro ao apagar contas: ' + accErr.message);
}

async function deleteTransactionsOnly(accountId: string, tenantId: string) {
  const { error } = await supabase
    .from('transactions')
    .delete()
    .eq('account_id', accountId)
    .eq('tenant_id', tenantId);
  if (error) throw new Error('Erro ao apagar transações: ' + error.message);

  // Zera o saldo da conta
  await supabase
    .from('accounts')
    .update({ balance_cents: 0 } as never)
    .eq('id', accountId);
}

const ACCOUNT_TYPES = [
  { value: 'checking',      label: 'Conta corrente' },
  { value: 'savings',       label: 'Conta poupança' },
  { value: 'cash',          label: 'Caixa físico' },
  { value: 'credit_card',   label: 'Cartão de crédito' },
  { value: 'investment',    label: 'Investimento' },
  { value: 'other',         label: 'Outro' },
];

function translateAccountError(msg: string): string {
  if (msg.includes('duplicate') || msg.includes('unique')) return 'Já existe uma conta com esse nome.';
  if (msg.includes('row-level security') || msg.includes('RLS')) return 'Permissão insuficiente para criar conta.';
  return 'Erro ao salvar. Verifique sua conexão e tente novamente.';
}

interface NewAccountModalProps {
  tenantId: string;
  onCreated: () => void;
  onClose: () => void;
}

function NewAccountModal({ tenantId, onCreated, onClose }: NewAccountModalProps) {
  const [name, setName]               = useState('');
  const [type, setType]               = useState('checking');
  const [institution, setInstitution] = useState('');
  const [balanceStr, setBalanceStr]   = useState('0,00');
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState('');
  const [nameErr, setNameErr]         = useState('');

  async function handleSave() {
    if (!name.trim()) { setNameErr('Nome é obrigatório.'); return; }
    setSaving(true);
    setErr('');
    const balanceCents = Math.round(parseFloat(balanceStr.replace(',', '.')) * 100) || 0;
    const { error } = await supabase.from('accounts').insert({
      tenant_id: tenantId,
      name: name.trim(),
      type,
      institution: institution.trim() || null,
      balance_cents: balanceCents,
    } as never);
    if (error) {
      setErr(translateAccountError(error.message));
      setSaving(false);
      return;
    }
    onCreated();
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card p-6 max-w-md w-full shadow-2xl space-y-4">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-semibold text-ink">Nova conta</h2>
          <button onClick={onClose} className="btn-ghost !p-1.5"><X className="w-4 h-4" /></button>
        </div>

        {err && (
          <div className="px-4 py-3 rounded-xl border border-negative/30 bg-negative/5 text-sm text-negative">{err}</div>
        )}

        <div>
          <label className="block text-xs text-ink-muted mb-1">Nome da conta *</label>
          <input
            className={`input w-full ${nameErr ? 'border-negative' : ''}`}
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameErr) setNameErr(''); }}
            placeholder="ex: Nubank, Caixa loja, Bradesco PJ"
            maxLength={80}
          />
          {nameErr && <p className="text-xs text-negative mt-1">{nameErr}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-ink-muted mb-1">Tipo *</label>
            <select className="input w-full" value={type} onChange={(e) => setType(e.target.value)}>
              {ACCOUNT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">Saldo inicial (R$)</label>
            <input
              className="input w-full"
              value={balanceStr}
              onChange={(e) => setBalanceStr(e.target.value)}
              placeholder="0,00"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs text-ink-muted mb-1">Instituição (opcional)</label>
          <input
            className="input w-full"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            placeholder="ex: Nubank, Bradesco"
            maxLength={100}
          />
        </div>

        <div className="flex gap-3 justify-end pt-1">
          <button onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
          <button onClick={() => void handleSave()} disabled={saving || !name.trim()} className="btn-primary gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Criar conta
          </button>
        </div>
      </div>
    </div>
  );
}

function iconFor(institution: string | null) {
  if (!institution) return Wallet;
  const l = institution.toLowerCase();
  if (l.includes('bradesco') || l.includes('nubank') || l.includes('inter') || l.includes('cartão')) return CreditCard;
  return Building;
}

// ─── Componente principal ─────────────────────────────────────────────────────

type ConfirmAction =
  | { type: 'account'; account: Account }
  | { type: 'transactions'; account: Account }
  | { type: 'all' };

export function ManageAccounts() {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const { accounts, loading, refetch: refetchAccounts } = useAccounts();
  const { items: pluggyItems, reload: reloadPluggy, getConnectToken, registerItem, syncNow, disconnect } = usePluggy();
  const [txCounts, setTxCounts] = useState<Record<string, number>>({});
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewAccount, setShowNewAccount] = useState(false);

  // ─── Open Finance (Pluggy) ────────────────────────────────────────────────
  const [connectToken, setConnectToken] = useState<string | null>(null);
  const [pluggyBusy, setPluggyBusy] = useState<'connect' | 'register' | 'sync' | string | null>(null);

  async function handleOpenWidget(reconnectItemId?: string) {
    setError('');
    setPluggyBusy('connect');
    try {
      const token = await getConnectToken(reconnectItemId);
      setConnectToken(token);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao iniciar conexão com o banco.');
    } finally {
      setPluggyBusy(null);
    }
  }

  async function handleWidgetSuccess(itemData: { item: { id: string } }) {
    setConnectToken(null);
    setPluggyBusy('register');
    setError('');
    try {
      await registerItem(itemData.item.id);
      setSuccess('Banco conectado! Contas criadas e transações dos últimos 90 dias sincronizadas.');
      refetchAccounts();
      await reloadPluggy();
    } catch (e: any) {
      setError(e.message ?? 'Banco autorizado, mas houve erro ao importar os dados. Use "Sincronizar agora".');
      await reloadPluggy();
    } finally {
      setPluggyBusy(null);
    }
  }

  async function handleSyncNow() {
    setPluggyBusy('sync');
    setError('');
    setSuccess('');
    try {
      await syncNow();
      setSuccess('Sincronização concluída.');
      refetchAccounts();
      await reloadPluggy();
    } catch (e: any) {
      setError(e.message ?? 'Erro ao sincronizar.');
    } finally {
      setPluggyBusy(null);
    }
  }

  async function handleDisconnect(itemId: string) {
    setPluggyBusy(itemId);
    setError('');
    try {
      await disconnect(itemId);
      setSuccess('Banco desconectado. As contas e o histórico foram mantidos.');
      refetchAccounts();
      await reloadPluggy();
    } catch (e: any) {
      setError(e.message ?? 'Erro ao desconectar.');
    } finally {
      setPluggyBusy(null);
    }
  }

  // Conta transações por conta
  useEffect(() => {
    if (!tenantId || accounts.length === 0) return;
    accounts.forEach((a) => {
      supabase
        .from('transactions')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', a.id)
        .eq('tenant_id', tenantId)
        .then(({ count }) => {
          setTxCounts((prev) => ({ ...prev, [a.id]: count ?? 0 }));
        });
    });
  }, [tenantId, accounts]);

  async function handleConfirm() {
    if (!confirm || !tenantId) return;
    setActionLoading(true);
    setError('');
    try {
      if (confirm.type === 'account') {
        await deleteAccount(confirm.account.id, tenantId);
        setSuccess(`Conta "${confirm.account.name}" e suas transações foram apagadas.`);
      } else if (confirm.type === 'transactions') {
        await deleteTransactionsOnly(confirm.account.id, tenantId);
        setSuccess(`Transações da conta "${confirm.account.name}" apagadas. Conta mantida com saldo R$ 0,00.`);
      } else if (confirm.type === 'all') {
        await deleteAllData(tenantId);
        setSuccess('Todos os dados foram apagados.');
      }
      setConfirm(null);
      refetchAccounts();
    } catch (err: any) {
      setError(err.message ?? 'Erro desconhecido');
    } finally {
      setActionLoading(false);
    }
  }

  const totalBalance = accounts.reduce((s, a) => s + a.balance_cents, 0);

  return (
    <div className="h-full flex bg-surface">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-y-auto">
          <div className="px-4 lg:px-8 py-6 max-w-3xl mx-auto">

            <button onClick={() => navigate('/financeiro')} className="btn-ghost mb-4 -ml-1">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao financeiro
            </button>

            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="font-display font-bold text-2xl text-ink mb-1">Gerenciar contas</h1>
                <p className="text-sm text-ink-muted">
                  Crie ou exclua contas e transações para organizar seu financeiro.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => void handleOpenWidget()}
                  disabled={pluggyBusy === 'connect'}
                  className="btn-secondary gap-2"
                >
                  {pluggyBusy === 'connect'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Landmark className="w-4 h-4" />}
                  Conectar banco
                </button>
                <button onClick={() => setShowNewAccount(true)} className="btn-primary gap-2">
                  <Plus className="w-4 h-4" />Nova conta
                </button>
              </div>
            </div>

            {/* Feedback */}
            {success && (
              <div className="rounded-xl border border-positive/30 bg-positive/5 p-4 text-sm text-positive mb-4">
                {success}
              </div>
            )}
            {error && (
              <div className="rounded-xl border border-negative/30 bg-negative/5 p-4 text-sm text-negative mb-4">
                {error}
              </div>
            )}

            {/* Bancos conectados via Open Finance */}
            {(pluggyItems.length > 0 || pluggyBusy === 'register') && (
              <div className="card p-5 mb-6">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-brand-700" />
                    <p className="font-semibold text-ink">Bancos conectados (Open Finance)</p>
                  </div>
                  <button
                    onClick={() => void handleSyncNow()}
                    disabled={pluggyBusy === 'sync' || pluggyItems.length === 0}
                    className="btn-secondary !py-1.5 !text-xs gap-1.5"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${pluggyBusy === 'sync' ? 'animate-spin' : ''}`} />
                    {pluggyBusy === 'sync' ? 'Sincronizando…' : 'Sincronizar agora'}
                  </button>
                </div>

                {pluggyBusy === 'register' && (
                  <p className="text-sm text-ink-muted flex items-center gap-2 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importando contas e transações do banco…
                  </p>
                )}

                <div className="space-y-2">
                  {pluggyItems.map((it) => {
                    const hasError = !!it.error_message || (it.status !== null && it.status !== 'UPDATED' && it.status !== 'UPDATING');
                    return (
                      <div key={it.id} className="flex items-center gap-3 rounded-xl bg-surface-subtle px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-ink truncate">
                            {it.connector_name ?? 'Banco'}
                            {hasError && <span className="chip-negative ml-2 text-[11px]">Atenção</span>}
                          </p>
                          <p className="text-xs text-ink-muted mt-0.5">
                            {hasError
                              ? (it.error_message ?? `Status: ${it.status}`)
                              : it.last_synced_at
                                ? `Última sincronização: ${formatDate(it.last_synced_at)}`
                                : 'Aguardando primeira sincronização'}
                          </p>
                        </div>
                        {hasError && (
                          <button
                            onClick={() => void handleOpenWidget(it.pluggy_item_id)}
                            className="btn-secondary !py-1 !px-2.5 !text-xs"
                          >
                            Reconectar
                          </button>
                        )}
                        <button
                          onClick={() => void handleDisconnect(it.pluggy_item_id)}
                          disabled={pluggyBusy === it.pluggy_item_id}
                          className="btn-secondary !py-1 !px-2.5 !text-xs gap-1 text-negative border-negative/30"
                          title="Revogar a conexão (contas e histórico são mantidos)"
                        >
                          {pluggyBusy === it.pluggy_item_id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Link2Off className="w-3 h-3" />}
                          Desconectar
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Zerar tudo */}
            <div className="card p-5 mb-6 border-negative/20">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-ink">Apagar todos os dados</p>
                  <p className="text-xs text-ink-muted mt-0.5">
                    Remove todas as contas e todas as transações. Use para recomeçar do zero.
                  </p>
                </div>
                <button
                  onClick={() => setConfirm({ type: 'all' })}
                  className="btn bg-negative hover:bg-negative/90 text-white shrink-0"
                  disabled={accounts.length === 0}
                >
                  <Trash2 className="w-4 h-4" />
                  Zerar tudo
                </button>
              </div>
            </div>

            {/* Lista de contas */}
            <h2 className="font-semibold text-ink mb-3">
              {loading ? 'Carregando…' : `${accounts.length} conta(s) · Saldo total: ${formatBRL(totalBalance)}`}
            </h2>

            {loading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-surface-subtle animate-pulse" />)}
              </div>
            ) : accounts.length === 0 ? (
              <div className="card p-8 text-center text-ink-muted text-sm">
                Nenhuma conta cadastrada.
              </div>
            ) : (
              <div className="space-y-3">
                {accounts.map((a) => {
                  const Icon = iconFor(a.institution);
                  const count = txCounts[a.id] ?? '…';
                  return (
                    <div key={a.id} className="card p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-ink">
                            {a.name}
                            {a.auto_sync_provider === 'pluggy' && (
                              <span className="chip bg-positive/10 text-positive ml-2 text-[11px]">
                                Sincronização automática
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-ink-muted">{a.institution ?? 'Caixa físico'}</p>
                          <div className="flex gap-4 mt-1">
                            <span className="text-sm font-semibold tabular-nums">
                              {formatBRL(a.balance_cents)}
                            </span>
                            <span className="text-xs text-ink-soft">{count} transações</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <button
                            onClick={() => setConfirm({ type: 'transactions', account: a })}
                            className="btn-secondary !py-1.5 !text-xs gap-1.5"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Apagar transações
                          </button>
                          <button
                            onClick={() => setConfirm({ type: 'account', account: a })}
                            className="btn bg-negative/10 hover:bg-negative/20 text-negative !py-1.5 !text-xs gap-1.5"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Apagar conta
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Pluggy Connect Widget */}
      {connectToken && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <Loader2 className="w-8 h-8 animate-spin text-white" />
          </div>
        }>
          <PluggyConnect
            connectToken={connectToken}
            includeSandbox={true}
            onSuccess={(data: { item: { id: string } }) => void handleWidgetSuccess(data)}
            onError={() => { setConnectToken(null); setError('A conexão com o banco não foi concluída.'); }}
            onClose={() => setConnectToken(null)}
          />
        </Suspense>
      )}

      {showNewAccount && tenantId && (
        <NewAccountModal
          tenantId={tenantId}
          onCreated={() => { setShowNewAccount(false); setSuccess('Conta criada com sucesso.'); refetchAccounts(); }}
          onClose={() => setShowNewAccount(false)}
        />
      )}

      {/* Confirm dialog */}
      {confirm && (
        <ConfirmDialog
          loading={actionLoading}
          message={
            confirm.type === 'all'
              ? `Tem certeza? Isso apagará TODAS as ${accounts.length} conta(s) e todas as transações. Esta ação não pode ser desfeita.`
              : confirm.type === 'account'
              ? `Apagar a conta "${confirm.account.name}" e TODAS as suas transações (${txCounts[confirm.account.id] ?? '?'})? Esta ação não pode ser desfeita.`
              : `Apagar todas as transações da conta "${confirm.account.name}" e zerar o saldo? A conta em si será mantida.`
          }
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
